// frontend/src/App.js  — full replacement
import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Clients from './components/Clients';
import ChargeSession from './components/ChargeSession';
import Transactions from './components/Transactions';
import SessionTypes from './components/SessionTypes';
import CreditPackages from './components/CreditPackages';
import RolesAccess from './components/RolesAccess';
import AthleteStats from './components/AthleteStats';
import Media from './components/Media';
import LoadCredits from './components/LoadCredits';
import Integrations from './components/Integrations';
import NotificationSettings from './components/NotificationSettings';
import CoachInfo from './components/CoachInfo';
import RecruitProfile from './components/RecruitProfile';
import ParentApp from './components/parent/ParentApp';
import ParentSetPassword from './components/parent/ParentSetPassword';
import AdminSetPassword from './components/AdminSetPassword';
import Overlay from './components/Overlay';
import styles from './App.module.css';

const API = '/api';

const VIEW_LABELS = {
  dashboard:    'Dashboard',
  clients:      'Client Accounts',
  charge:       'Charge Session',
  loadcredits:  'Load Credits',
  transactions: 'Transaction History',
  stats:        'Athlete Stats',
  media:        'Media',
  sessiontypes: 'Session Types',
  creditpackages: 'Credit Packages',
  roles: 'Roles & Access',
  notifications: 'Notifications',
  coachinfo:    'Coach Info',
  integrations: 'Integrations',
};

// Capability each view requires (see docs/admin-roles-design.md). Owners bypass.
// Order matters: the first accessible view is the fallback landing page.
const VIEW_CAP = {
  dashboard:     'view',
  clients:       'view',
  stats:         'view',
  transactions:  'view',
  charge:        'charge',
  loadcredits:   'credits',
  media:         'media_manage',
  sessiontypes:  'settings_manage',
  creditpackages:'settings_manage',
  notifications: 'settings_manage',
  coachinfo:     'settings_manage',
  integrations:  'integrations_manage',
  roles:         'team_manage',
};

// ── Change Password Modal ─────────────────────────────────────────────────────
function ChangePasswordModal({ onClose, authHeader, showToast }) {
  const [current,  setCurrent]  = React.useState('');
  const [next,     setNext]     = React.useState('');
  const [confirm,  setConfirm]  = React.useState('');
  const [loading,  setLoading]  = React.useState(false);
  const [error,    setError]    = React.useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setError('New passwords do not match.'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to change password.'); return; }
      showToast('Password changed successfully');
      onClose();
    } catch {
      setError('Network error — try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay className={styles.overlay} onClose={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Change Password</h3>
          <button className={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        <form className={styles.modalBody} onSubmit={handleSubmit} noValidate>
          <div className={styles.modalField}>
            <label className={styles.modalLabel}>Current Password</label>
            <input
              className={styles.modalInput}
              type="password"
              value={current}
              onChange={e => setCurrent(e.target.value)}
              autoComplete="current-password"
              disabled={loading}
              required
            />
          </div>
          <div className={styles.modalField}>
            <label className={styles.modalLabel}>New Password</label>
            <input
              className={styles.modalInput}
              type="password"
              value={next}
              onChange={e => setNext(e.target.value)}
              autoComplete="new-password"
              disabled={loading}
              required
              minLength={8}
              placeholder="Minimum 8 characters"
            />
          </div>
          <div className={styles.modalField}>
            <label className={styles.modalLabel}>Confirm New Password</label>
            <input
              className={styles.modalInput}
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="new-password"
              disabled={loading}
              required
            />
          </div>
          {error && <div className={styles.modalError}>{error}</div>}
          <button
            type="submit"
            className={styles.modalSubmit}
            disabled={loading || !current || !next || !confirm}
          >
            {loading ? 'Saving...' : 'Update Password'}
          </button>
        </form>
      </div>
    </Overlay>
  );
}

// ── Invite Admin Modal ────────────────────────────────────────────────────────
function InviteAdminModal({ onClose, authHeader, showToast }) {
  const [email, setEmail]           = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [roles, setRoles]           = React.useState([]);
  const [roleId, setRoleId]         = React.useState('');
  const [makeOwner, setMakeOwner]   = React.useState(false);
  const [loading, setLoading]       = React.useState(false);
  const [error, setError]           = React.useState('');
  const [done, setDone]             = React.useState(false);

  React.useEffect(() => {
    fetch('/api/config/admin-roles', { headers: authHeader() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (Array.isArray(d?.roles)) setRoles(d.roles); })
      .catch(() => {});
  }, [authHeader]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Email is required'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/admin/invite-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          email: email.trim(),
          displayName: displayName.trim() || null,
          roleId: makeOwner ? null : (roleId || null),
          isOwner: makeOwner,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to send invite.'); return; }
      showToast(`Invite sent to ${data.email}`);
      setDone(true);
    } catch {
      setError('Network error — try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay className={styles.overlay} onClose={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Invite Admin</h3>
          <button className={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        {done ? (
          <div className={styles.modalBody} style={{ alignItems: 'center', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', color: 'var(--green)' }}>✓</div>
            <p style={{ color: 'var(--gray-1)', fontSize: '0.92rem' }}>
              Invite sent. They have 72 hours to set up their admin account.
            </p>
            <button className={styles.modalSubmit} onClick={onClose}>Done</button>
          </div>
        ) : (
          <form className={styles.modalBody} onSubmit={handleSubmit} noValidate>
            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Admin Email</label>
              <input
                className={styles.modalInput}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                disabled={loading}
                placeholder="newadmin@example.com"
                required
              />
            </div>
            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Display Name (optional)</label>
              <input
                className={styles.modalInput}
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                disabled={loading}
                placeholder="What they'll see in the portal"
              />
            </div>
            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Access</label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.85rem', color: 'var(--gray-2)', marginBottom: makeOwner ? 0 : 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={makeOwner} onChange={e => setMakeOwner(e.target.checked)} disabled={loading}
                  style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--orange)' }} />
                <span><strong style={{ color: 'var(--gray-1)' }}>Owner</strong> — full access &amp; can manage the team</span>
              </label>
              {!makeOwner && (
                <select className={styles.modalInput} value={roleId} onChange={e => setRoleId(e.target.value)} disabled={loading}>
                  <option value="">— Assign a role (optional) —</option>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              )}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--gray-3)', lineHeight: 1.5 }}>
              They'll receive an email with a one-time link to set their password.
              You can change their role or owner status anytime from Roles &amp; Access.
            </div>
            {error && <div className={styles.modalError}>{error}</div>}
            <button type="submit" className={styles.modalSubmit} disabled={loading || !email}>
              {loading ? 'Sending invite...' : 'Send Invite'}
            </button>
          </form>
        )}
      </div>
    </Overlay>
  );
}

// ── Inner app (only rendered when authenticated) ─────────────────────────────
function AdminApp() {
  const { user, logout, authHeader } = useAuth();
  const [nav, setNav] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const initialView = params.get('view');
    return { view: initialView || 'dashboard', clientId: null };
  });
  const [showChangePw, setShowChangePw] = useState(false);
  const [showInviteAdmin, setShowInviteAdmin] = useState(false);

  const handleNavigate = (newView, clientId = null) => {
    setNav({ view: newView, clientId: clientId || null });
  };

  const view             = nav.view;
  const selectedClientId = nav.clientId;
  const [clients, setClients]           = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [config, setConfig]             = useState(null);
  const [loading, setLoading]           = useState(true);
  const [toast, setToast]               = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchAll = async () => {
    try {
      const headers = authHeader();
      const [c, t, cfg] = await Promise.all([
        fetch(`${API}/clients`,      { headers }).then(r => r.json()),
        fetch(`${API}/transactions`, { headers }).then(r => r.json()),
        fetch(`${API}/config`,       { headers }).then(r => r.json()),
      ]);
      setClients(c);
      setTransactions(t);
      setConfig(cfg);
    } catch {
      showToast('Could not connect to backend. Is the server running?', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const views = {
    dashboard:    Dashboard,
    clients:      Clients,
    charge:       ChargeSession,
    loadcredits:  LoadCredits,
    transactions: Transactions,
    stats:        AthleteStats,
    media:        Media,
    sessiontypes: SessionTypes,
    creditpackages: CreditPackages,
    roles: RolesAccess,
    notifications: NotificationSettings,
    coachinfo:    CoachInfo,
    integrations: Integrations,
  };

  const hasCap = (cap) => !!(user?.isOwner || (user?.permissions || []).includes(cap));
  // If the current view isn't permitted for this admin's role, fall back to the
  // first view they can access (keeps a limited role off pages that would 403).
  const firstAccessibleView = Object.keys(VIEW_CAP).find(v => hasCap(VIEW_CAP[v])) || 'dashboard';
  const effView = (VIEW_CAP[view] && !hasCap(VIEW_CAP[view])) ? firstAccessibleView : view;
  const ViewComponent = views[effView] || Dashboard;

  return (
    <div className={styles.app}>
      <Sidebar currentView={effView} onNavigate={handleNavigate} clients={clients} hasCap={hasCap} />
      <main className={styles.main}>
        <header className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <span className={styles.viewLabel}>{VIEW_LABELS[effView] || 'Dashboard'}</span>
          </div>
          <div className={styles.topbarRight}>
            <span className={styles.userName}>
              <span className={styles.onlineDot} />
              Logged in: {user?.displayName || user?.username}
            </span>
            <button className={styles.userBadge} onClick={() => setShowInviteAdmin(true)} title="Invite another admin">
              Invite Admin
            </button>
            <button className={styles.userBadge} onClick={() => setShowChangePw(true)} title="Change password">
              Change Password
            </button>
            <button className={styles.logoutBtn} onClick={logout} title="Sign out">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign out
            </button>
          </div>
        </header>

        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <p>Loading NGS data...</p>
          </div>
        ) : (
          <ViewComponent
            clients={clients}
            transactions={transactions}
            config={config}
            refresh={fetchAll}
            showToast={showToast}
            onNavigate={handleNavigate}
            API={API}
            initialClientId={selectedClientId}
            autoOpenLoad={false}
            authHeader={authHeader}
            currentUser={user}
          />
        )}
      </main>

      {toast && (
        <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
          <span>{toast.type === 'success' ? '✓' : '✕'}</span>
          {toast.msg}
        </div>
      )}

      {showChangePw && (
        <ChangePasswordModal
          onClose={() => setShowChangePw(false)}
          authHeader={authHeader}
          showToast={showToast}
        />
      )}

      {showInviteAdmin && (
        <InviteAdminModal
          onClose={() => setShowInviteAdmin(false)}
          authHeader={authHeader}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ── Auth gate ─────────────────────────────────────────────────────────────────
function AuthGate() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0d0d0d',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          border: '3px solid rgba(192,74,14,0.2)',
          borderTopColor: '#C04A0E',
          animation: 'spin 0.7s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return user ? <AdminApp /> : <Login />;
}

// ── Root export ───────────────────────────────────────────────────────────────
export default function App() {
  const path = window.location.pathname;
  const recruitMatch = path.match(/^\/athlete\/([a-f0-9]+)$/);
  if (recruitMatch) {
    return <RecruitProfile token={recruitMatch[1]} />;
  }

  const inviteMatch = path.match(/^\/parent\/invite\/([a-f0-9]+)$/);
  if (inviteMatch) {
    return <ParentSetPassword token={inviteMatch[1]} />;
  }

  const adminSetPwMatch = path.match(/^\/admin\/set-password\/([a-f0-9]+)$/);
  if (adminSetPwMatch) {
    return <AdminSetPassword token={adminSetPwMatch[1]} />;
  }

  if (path.startsWith('/parent')) {
    return <ParentApp />;
  }

  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}
